// Procedural sampler instruments and drum one-shots (§13.4). Each instrument note is rendered once
// per reference pitch (every C its range needs, so playback never repitches by more than ±6
// semitones) and each drum once, with the §13.2 kit; the sequencer repitches with playbackRate.
// Pads are live voices (music/pad.ts), not rendered here.
import type { Kit } from '../sfx/kit';
import type { DrumId, InstrumentId } from './songTypes';

export interface InstrumentDef {
  /** MIDI range the songs and stingers use. */
  lo: number;
  hi: number;
  /** Seconds rendered per reference note. */
  dur: number;
  /** Damping/release time at note-off, seconds. */
  release: number;
  /** True for held instruments (organ-like); struck ones ring and are damped at note-off. */
  held: boolean;
  /** Level trim, dB. */
  db: number;
  render(k: Kit, f: number): void;
}

export interface DrumDef { dur: number; db: number; render(k: Kit): void }

const cents = (f: number, c: number): number => f * (2 ** (c / 1200) - 1);

/** A held-note envelope: attack, a short settle to `s`, hold until `dur − r`, release to zero. */
function heldEnv(k: Kit, a: number, s: number, r: number, peak = 1): GainNode {
  const d = 0.08;
  return k.env({ a, d, s, hold: Math.max(0, k.dur - a - d - r - 0.01), r, peak });
}

/** Delayed vibrato: depth fades in after `delay`. */
function vibrato(k: Kit, params: AudioParam[], f: number, rate: number, depthCents: number, delay: number): void {
  const lfo = k.osc('sine', rate);
  for (const p of params) {
    const g = k.gain(0);
    g.gain.setValueAtTime(0, 0);
    g.gain.setValueAtTime(0, delay);
    g.gain.linearRampToValueAtTime(cents(f, depthCents), delay + 0.25);
    lfo.connect(g).connect(p);
  }
}

/** Pulse wave Fourier series (duty `d`) as PeriodicWave cosine terms. */
function pulseSeries(d: number, n = 32): number[] {
  const out: number[] = [];
  for (let i = 1; i <= n; i++) out.push(((2 / (i * Math.PI)) * Math.sin(i * Math.PI * d)) * (1 - i / (n + 1)));
  return out;
}

/** Classic 808 metal: six detuned squares (cymbals, hats, cowbell colour). */
function metal(k: Kit, scale = 1, at = 0, stop?: number): GainNode {
  const mix = k.gain(1 / 6);
  for (const f of [205.3, 304.4, 369.6, 522.7, 540, 800]) k.osc('square', f * scale, at, stop).connect(mix);
  return mix;
}

function noiseHit(k: Kit, color: 'white' | 'pink' | 'brown', filter: BiquadFilterNode, a: number, d: number, peak: number, at = 0): void {
  k.toOut(k.noise(color, at, at + a + d + 0.02), filter, k.env({ a, d, s: 0, r: 0, peak, at }));
}

function sineDrop(k: Kit, from: number, to: number, drop: number, a: number, d: number, peak: number, at = 0): OscillatorNode {
  const o = k.osc('sine', from, at, at + a + d + 0.02);
  k.pitchEnv(o.frequency, from, to, drop, 'exp', at);
  k.toOut(o, k.env({ a, d, s: 0, r: 0, peak, at }));
  return o;
}

export const INSTRUMENTS: Readonly<Record<Exclude<InstrumentId, 'pad'>, InstrumentDef>> = {
  // Rhodes-style electric piano: FM bark that mellows, a faint tine ping, gentle tremolo.
  ep: {
    lo: 50, hi: 84, dur: 2.4, release: 0.12, held: false, db: -1,
    render(k, f) {
      const c = k.fm(f, 1, 1.5, 0.7);
      const trem = k.gain(0.9);
      k.lfo(4.6, 0.08, trem.gain);
      k.toOut(c, k.lp(Math.min(9000, f * 9), 0.6), trem, k.env({ a: 0.002, d: 2.3, s: 0, r: 0, peak: 0.7 }));
      const tine = k.osc('sine', f * 7.02);
      k.toOut(tine, k.env({ a: 0.0008, d: 0.06, s: 0, r: 0, peak: 0.06 }));
      const body = k.osc('sine', f * 2);
      k.toOut(body, k.env({ a: 0.002, d: 0.5, s: 0, r: 0, peak: 0.1 }));
    },
  },
  // Upright-ish FM bass: a growling pluck with a finger thump.
  upright: {
    lo: 28, hi: 55, dur: 1.6, release: 0.08, held: false, db: 0,
    render(k, f) {
      const c = k.fm(f, 1, 1.1, 0.22);
      k.pitchEnv(c.frequency, f * 1.012, f, 0.03);
      k.toOut(c, k.lp(Math.min(2400, f * 14), 0.7), k.env({ a: 0.004, d: 1.4, s: 0, r: 0, peak: 0.95 }));
      const h2 = k.osc('triangle', f * 2);
      k.toOut(h2, k.env({ a: 0.003, d: 0.35, s: 0, r: 0, peak: 0.16 }));
      noiseHit(k, 'pink', k.lp(380, 0.8), 0.001, 0.03, 0.22);
    },
  },
  marimba: {
    lo: 55, hi: 91, dur: 1.0, release: 0.1, held: false, db: -1,
    render(k, f) {
      k.toOut(k.osc('sine', f), k.env({ a: 0.0015, d: 0.85, s: 0, r: 0, peak: 0.75 }));
      k.toOut(k.osc('sine', f * 4), k.env({ a: 0.001, d: 0.14, s: 0, r: 0, peak: 0.22 }));
      k.toOut(k.osc('sine', f * 9.9), k.env({ a: 0.0008, d: 0.025, s: 0, r: 0, peak: 0.06 }));
      noiseHit(k, 'white', k.bp(Math.min(6000, f * 6), 2), 0.0005, 0.006, 0.12);
    },
  },
  // Harmon-muted trumpet: a saw through a nasal formant pair, scoop and delayed vibrato.
  mutedTrumpet: {
    lo: 55, hi: 86, dur: 2.4, release: 0.1, held: true, db: -2,
    render(k, f) {
      const o = k.osc('sawtooth', f);
      k.pitchEnv(o.frequency, f * 0.985, f, 0.04);
      vibrato(k, [o.frequency], f, 5.2, 18, 0.18);
      const sum = k.gain(1);
      const b1 = k.bp(1450, 2.2);
      const b2 = k.bp(2700, 3.2);
      const g2 = k.gain(0.55);
      o.connect(b1).connect(sum);
      o.connect(b2).connect(g2).connect(sum);
      const swell = k.lp(1600, 0.8);
      k.pitchEnv(swell.frequency, 1600, 4200, 0.06);
      k.toOut(sum, swell, k.hp(380), heldEnv(k, 0.025, 0.8, 0.09, 1.6));
    },
  },
  // Slap-FM bass: bright metallic attack, octave "pop" partial.
  slapBass: {
    lo: 28, hi: 55, dur: 1.2, release: 0.06, held: false, db: 0,
    render(k, f) {
      const c = k.fm(f, 1, 3.2, 0.11);
      k.toOut(c, k.lp(Math.min(5000, f * 30), 0.8), k.env({ a: 0.001, d: 0.22, s: 0.35, hold: 0.25, r: 0.6, peak: 0.85 }));
      const pop = k.fm(f * 2, 2, 1.5, 0.06);
      k.toOut(pop, k.env({ a: 0.001, d: 0.09, s: 0, r: 0, peak: 0.25 }));
      noiseHit(k, 'white', k.hp(2400), 0.0005, 0.008, 0.18);
    },
  },
  // Gated synthwave saw chord voice: two detuned saws, a filter that closes after the attack.
  sawChord: {
    lo: 52, hi: 84, dur: 2.2, release: 0.05, held: true, db: -5,
    render(k, f) {
      const mix = k.gain(0.5);
      const a = k.osc('sawtooth', f);
      const b = k.osc('sawtooth', f);
      a.detune.value = -7;
      b.detune.value = 7;
      a.connect(mix);
      b.connect(mix);
      const lp = k.lp(4200, 1.2);
      k.pitchEnv(lp.frequency, 4200, 1900, 0.25);
      k.toOut(mix, lp, heldEnv(k, 0.004, 0.7, 0.04));
    },
  },
  pulseArp: {
    lo: 55, hi: 91, dur: 0.5, release: 0.04, held: false, db: -4,
    render(k, f) {
      const o = k.wave(pulseSeries(0.25), f);
      const lp = k.lp(6000, 2);
      k.pitchEnv(lp.frequency, 6000, 1400, 0.18);
      k.toOut(o, lp, k.env({ a: 0.002, d: 0.32, s: 0, r: 0, peak: 0.55 }));
    },
  },
  squareLead: {
    lo: 60, hi: 93, dur: 2.4, release: 0.08, held: true, db: -5,
    render(k, f) {
      const a = k.osc('square', f);
      const b = k.osc('square', f);
      b.detune.value = 8;
      k.pitchEnv(a.frequency, f * 0.982, f, 0.025);
      k.pitchEnv(b.frequency, f * 0.982, f, 0.025);
      vibrato(k, [a.frequency, b.frequency], f, 5.6, 14, 0.22);
      const mix = k.gain(0.5);
      a.connect(mix);
      b.connect(mix);
      k.toOut(mix, k.lp(3400, 0.9), heldEnv(k, 0.008, 0.78, 0.07));
    },
  },
  // Musette accordion: two reeds a few cents apart through a reedy formant, bellows shake.
  accordion: {
    lo: 55, hi: 84, dur: 2.0, release: 0.06, held: true, db: -4,
    render(k, f) {
      const mix = k.gain(0.5);
      const a = k.osc('sawtooth', f);
      const b = k.osc('sawtooth', f);
      b.detune.value = 13;
      a.connect(mix);
      b.connect(mix);
      const trem = k.gain(0.92);
      k.lfo(5.8, 0.08, trem.gain);
      const sum = k.gain(1);
      const f1 = k.bp(1100, 1.4);
      mix.connect(f1).connect(sum);
      const low = k.lp(Math.min(3200, f * 6), 0.7);
      const lg = k.gain(0.7);
      mix.connect(low).connect(lg).connect(sum);
      k.toOut(sum, trem, heldEnv(k, 0.018, 0.9, 0.05));
    },
  },
  tuba: {
    lo: 28, hi: 55, dur: 1.4, release: 0.08, held: true, db: 1,
    render(k, f) {
      const s = k.osc('sawtooth', f);
      k.pitchEnv(s.frequency, f * 0.985, f, 0.04);
      const lp = k.lp(Math.min(900, f * 5), 1.1);
      k.pitchEnv(lp.frequency, Math.min(1400, f * 8), Math.min(900, f * 5), 0.08);
      k.toOut(s, lp, heldEnv(k, 0.022, 0.75, 0.07, 0.8));
      const sub = k.osc('sine', f);
      k.toOut(sub, heldEnv(k, 0.02, 0.8, 0.07, 0.55));
    },
  },
  // Glockenspiel bar: inharmonic partials 1, 2.76, 5.40 and a hard mallet.
  glock: {
    lo: 67, hi: 100, dur: 1.6, release: 0.2, held: false, db: -6,
    render(k, f) {
      k.toOut(k.osc('sine', f), k.env({ a: 0.0008, d: 1.5, s: 0, r: 0, peak: 0.6 }));
      k.toOut(k.osc('sine', f * 2.76), k.env({ a: 0.0006, d: 0.3, s: 0, r: 0, peak: 0.16 }));
      k.toOut(k.osc('sine', f * 5.4), k.env({ a: 0.0005, d: 0.07, s: 0, r: 0, peak: 0.07 }));
      noiseHit(k, 'white', k.hp(5000), 0.0003, 0.004, 0.1);
    },
  },
  // Clavinet: a bright Karplus-Strong pluck through a funky resonant band.
  clav: {
    lo: 55, hi: 84, dur: 0.8, release: 0.03, held: false, db: -3,
    render(k, f) {
      const p = k.pluck(f, 0.78, 0.97);
      const sum = k.gain(1);
      // The funky resonance sits two octaves up (the 4th harmonic), so it never colours the harmony.
      const res = k.bp(Math.min(3200, f * 4), 2.5);
      const rg = k.gain(1.3);
      p.connect(res).connect(rg).connect(sum);
      const thin = k.hp(Math.max(200, f * 0.9), 0.7);
      p.connect(thin).connect(sum);
      k.toOut(sum, k.env({ a: 0.001, d: 0.12, s: 0.45, hold: 0.25, r: 0.3, peak: 1 }));
    },
  },
  whistle: {
    lo: 67, hi: 96, dur: 2.2, release: 0.08, held: true, db: -5,
    render(k, f) {
      const o = k.osc('sine', f);
      k.pitchEnv(o.frequency, f * 0.966, f, 0.045);
      vibrato(k, [o.frequency], f, 5.5, 22, 0.15);
      k.toOut(o, heldEnv(k, 0.03, 0.85, 0.07, 0.8));
      const air = k.noise('white');
      k.toOut(air, k.bp(f, 14), heldEnv(k, 0.03, 0.8, 0.07, 0.35));
      const h2 = k.osc('sine', f * 2);
      k.toOut(h2, heldEnv(k, 0.03, 0.8, 0.07, 0.04));
    },
  },
  // Brass section: three saws, a filter "blat" on the attack.
  brass: {
    lo: 48, hi: 96, dur: 2.0, release: 0.1, held: true, db: -4,
    render(k, f) {
      const mix = k.gain(0.33);
      for (const d of [-9, 0, 9]) {
        const o = k.osc('sawtooth', f);
        o.detune.value = d;
        k.pitchEnv(o.frequency, f * 0.98, f, 0.035);
        o.connect(mix);
      }
      const lp = k.lp(f * 2, 1.4);
      lp.frequency.setValueAtTime(f * 1.5, 0);
      lp.frequency.exponentialRampToValueAtTime(Math.min(12000, f * 10), 0.045);
      lp.frequency.exponentialRampToValueAtTime(Math.min(8000, f * 5), 0.25);
      k.toOut(mix, lp, k.distort(0.08), heldEnv(k, 0.018, 0.78, 0.09));
    },
  },
  trombone: {
    lo: 40, hi: 72, dur: 2.0, release: 0.12, held: true, db: -3,
    render(k, f) {
      const o = k.osc('sawtooth', f);
      vibrato(k, [o.frequency], f, 5, 25, 0.2);
      const lp = k.lp(f * 2, 1.6);
      lp.frequency.setValueAtTime(f * 1.4, 0);
      lp.frequency.exponentialRampToValueAtTime(Math.min(4000, f * 6), 0.12);
      k.toOut(o, lp, heldEnv(k, 0.04, 0.8, 0.1));
    },
  },
  synthBass: {
    lo: 28, hi: 55, dur: 1.4, release: 0.05, held: true, db: -1,
    render(k, f) {
      const s = k.osc('sawtooth', f);
      const q = k.osc('square', f * 0.5);
      const mix = k.gain(0.5);
      s.connect(mix);
      const qg = k.gain(0.6);
      q.connect(qg).connect(mix);
      const lp = k.lp(Math.min(3000, f * 24), 3.5);
      k.pitchEnv(lp.frequency, Math.min(3000, f * 24), Math.min(700, f * 6), 0.18);
      k.toOut(mix, lp, heldEnv(k, 0.003, 0.6, 0.05));
    },
  },
  bell: {
    lo: 55, hi: 100, dur: 1.8, release: 0.25, held: false, db: -8,
    render(k, f) {
      const c = k.fm(f, 3.5, 2.2, 1.2);
      k.toOut(c, k.env({ a: 0.001, d: 1.7, s: 0, r: 0, peak: 0.5 }));
      k.toOut(k.osc('sine', f * 2), k.env({ a: 0.001, d: 0.4, s: 0, r: 0, peak: 0.12 }));
    },
  },
  timpani: {
    lo: 36, hi: 60, dur: 2.0, release: 0.3, held: false, db: 0,
    render(k, f) {
      const o = k.osc('sine', f);
      k.pitchEnv(o.frequency, f * 1.03, f, 0.08);
      k.toOut(o, k.env({ a: 0.002, d: 1.9, s: 0, r: 0, peak: 0.8 }));
      k.toOut(k.osc('sine', f * 1.505), k.env({ a: 0.002, d: 0.7, s: 0, r: 0, peak: 0.25 }));
      k.toOut(k.osc('sine', f * 1.99), k.env({ a: 0.002, d: 0.4, s: 0, r: 0, peak: 0.12 }));
      noiseHit(k, 'brown', k.lp(500), 0.001, 0.08, 0.5);
    },
  },
  nylon: {
    lo: 45, hi: 84, dur: 1.6, release: 0.1, held: false, db: -3,
    render(k, f) {
      k.toOut(k.pluck(f, 1.58, 0.55), k.lp(Math.min(5000, f * 10), 0.6), k.gain(0.9));
    },
  },
  // The tonal half of the musical bonk (§13.4): a rubbery chirp that lands on the note.
  bonkTone: {
    lo: 58, hi: 92, dur: 0.45, release: 0.08, held: false, db: -1,
    render(k, f) {
      const o = k.osc('sine', f);
      k.pitchEnv(o.frequency, f * 2.4, f, 0.03);
      k.toOut(o, k.env({ a: 0.0015, d: 0.4, s: 0, r: 0, peak: 0.85 }));
      const t = k.osc('triangle', f * 2);
      k.pitchEnv(t.frequency, f * 4.4, f * 2, 0.025);
      k.toOut(t, k.env({ a: 0.001, d: 0.16, s: 0, r: 0, peak: 0.12 }));
      const r = k.fm(f * 3, 1.5, 1.2, 0.05);
      k.toOut(r, k.env({ a: 0.001, d: 0.05, s: 0, r: 0, peak: 0.06 }));
    },
  },
  // Crate-open / crown sparkle blips.
  sparkle: {
    lo: 72, hi: 103, dur: 0.35, release: 0.05, held: false, db: -6,
    render(k, f) {
      k.toOut(k.osc('sine', f), k.env({ a: 0.001, d: 0.3, s: 0, r: 0, peak: 0.6 }));
      k.toOut(k.osc('sine', f * 3), k.env({ a: 0.001, d: 0.06, s: 0, r: 0, peak: 0.1 }));
    },
  },
};

export const DRUMS: Readonly<Record<DrumId, DrumDef>> = {
  kick: {
    dur: 0.45, db: 0, render(k) {
      sineDrop(k, 165, 48, 0.09, 0.001, 0.4, 1);
      sineDrop(k, 330, 96, 0.04, 0.001, 0.05, 0.18);
      noiseHit(k, 'white', k.bp(3500, 1), 0.0003, 0.004, 0.25);
    },
  },
  kickSoft: {
    dur: 0.32, db: -2, render(k) {
      sineDrop(k, 112, 52, 0.07, 0.003, 0.28, 0.8);
      noiseHit(k, 'pink', k.lp(700), 0.002, 0.02, 0.18);
    },
  },
  kick909: {
    dur: 0.6, db: 0, render(k) {
      const o = k.osc('sine', 200);
      o.frequency.setValueAtTime(200, 0);
      o.frequency.exponentialRampToValueAtTime(70, 0.03);
      o.frequency.exponentialRampToValueAtTime(48, 0.18);
      k.toOut(o, k.distort(0.12), k.env({ a: 0.001, d: 0.55, s: 0, r: 0, peak: 0.95 }));
      noiseHit(k, 'white', k.hp(4000), 0.0003, 0.006, 0.35);
    },
  },
  kickPolka: {
    dur: 0.3, db: -1, render(k) {
      sineDrop(k, 135, 62, 0.05, 0.001, 0.24, 0.9);
      noiseHit(k, 'pink', k.bp(1300, 1.4), 0.0005, 0.015, 0.25);
    },
  },
  heartbeat: {
    dur: 0.4, db: 0, render(k) {
      const o = sineDrop(k, 74, 44, 0.09, 0.006, 0.32, 1);
      void o;
      noiseHit(k, 'brown', k.lp(180), 0.004, 0.1, 0.5);
    },
  },
  snare: {
    dur: 0.32, db: -2, render(k) {
      noiseHit(k, 'white', k.bp(2200, 0.9), 0.001, 0.18, 0.75);
      noiseHit(k, 'white', k.hp(5500), 0.001, 0.1, 0.3);
      sineDrop(k, 210, 178, 0.05, 0.001, 0.11, 0.5);
    },
  },
  snare909: {
    dur: 0.36, db: -2, render(k) {
      noiseHit(k, 'white', k.hp(1600), 0.001, 0.24, 0.6);
      sineDrop(k, 195, 180, 0.04, 0.001, 0.1, 0.4);
      sineDrop(k, 340, 320, 0.03, 0.001, 0.07, 0.25);
    },
  },
  snarePolka: {
    dur: 0.2, db: -3, render(k) {
      noiseHit(k, 'white', k.bp(3200, 1.2), 0.0008, 0.12, 0.7);
      sineDrop(k, 250, 230, 0.03, 0.001, 0.06, 0.35);
    },
  },
  brushTap: {
    dur: 0.16, db: -4, render(k) {
      noiseHit(k, 'white', k.bp(3600, 0.7), 0.004, 0.1, 0.55);
      noiseHit(k, 'pink', k.lp(1300), 0.002, 0.05, 0.2);
    },
  },
  brushSwish: {
    dur: 0.36, db: -8, render(k) {
      const n = k.noise('white');
      const bp = k.bp(2400, 1);
      k.pitchEnv(bp.frequency, 2400, 5200, 0.28);
      k.toOut(n, bp, k.env({ a: 0.09, d: 0.24, s: 0, r: 0, peak: 0.4 }));
    },
  },
  clap: {
    dur: 0.26, db: -3, render(k) {
      for (const t of [0, 0.011, 0.023]) noiseHit(k, 'white', k.bp(1250, 1.3), 0.0005, 0.008, 0.7, t);
      noiseHit(k, 'white', k.bp(1200, 1.1), 0.001, 0.16, 0.55, 0.03);
    },
  },
  rim: {
    dur: 0.08, db: -4, render(k) {
      noiseHit(k, 'white', k.bp(1900, 3), 0.0005, 0.03, 0.6);
      sineDrop(k, 830, 800, 0.02, 0.0005, 0.03, 0.45);
      sineDrop(k, 1660, 1600, 0.01, 0.0005, 0.016, 0.2);
    },
  },
  hat: {
    dur: 0.08, db: -9, render(k) {
      noiseHit(k, 'white', k.hp(7800), 0.0005, 0.045, 0.5);
      k.toOut(metal(k, 1, 0, 0.07), k.hp(8000), k.env({ a: 0.0005, d: 0.04, s: 0, r: 0, peak: 0.35 }));
    },
  },
  hatOpen: {
    dur: 0.42, db: -10, render(k) {
      noiseHit(k, 'white', k.hp(7200), 0.001, 0.34, 0.45);
      k.toOut(metal(k), k.hp(7500), k.env({ a: 0.001, d: 0.32, s: 0, r: 0, peak: 0.35 }));
    },
  },
  hatPedal: {
    dur: 0.06, db: -10, render(k) {
      noiseHit(k, 'white', k.hp(6200), 0.002, 0.03, 0.35);
      noiseHit(k, 'pink', k.bp(900, 1.5), 0.001, 0.02, 0.2);
    },
  },
  hat909: {
    dur: 0.08, db: -9, render(k) {
      k.toOut(metal(k, 1.6, 0, 0.07), k.bp(10000, 0.8), k.hp(7000), k.env({ a: 0.0005, d: 0.05, s: 0, r: 0, peak: 0.6 }));
      noiseHit(k, 'white', k.hp(9000), 0.0005, 0.04, 0.3);
    },
  },
  ride: {
    dur: 1.4, db: -12, render(k) {
      k.toOut(metal(k, 1.3), k.bp(5200, 0.8), k.env({ a: 0.001, d: 1.3, s: 0, r: 0, peak: 0.35 }));
      const ping = k.fm(3150, 1.41, 1.6, 0.5);
      k.toOut(ping, k.env({ a: 0.001, d: 0.6, s: 0, r: 0, peak: 0.12 }));
      noiseHit(k, 'white', k.hp(6500), 0.001, 0.25, 0.12);
    },
  },
  shaker: {
    dur: 0.1, db: -12, render(k) {
      noiseHit(k, 'white', k.bp(6800, 1.6), 0.012, 0.06, 0.6);
    },
  },
  tambourine: {
    dur: 0.18, db: -11, render(k) {
      noiseHit(k, 'white', k.hp(7200), 0.001, 0.09, 0.45);
      noiseHit(k, 'white', k.hp(7800), 0.001, 0.06, 0.25, 0.018);
      for (const f of [5600, 7300, 8500, 9200]) {
        const o = k.osc('sine', f);
        k.lfo(43, f * 0.01, o.frequency);
        k.toOut(o, k.env({ a: 0.001, d: 0.12, s: 0, r: 0, peak: 0.05 }));
      }
    },
  },
  crash: {
    dur: 2.0, db: -11, render(k) {
      k.toOut(metal(k, 1.1), k.hp(3800), k.env({ a: 0.002, d: 1.9, s: 0, r: 0, peak: 0.45 }));
      noiseHit(k, 'white', k.hp(4500), 0.002, 1.6, 0.45);
    },
  },
  cowbell: {
    dur: 0.3, db: -9, render(k) {
      const mix = k.gain(0.5);
      k.osc('square', 562).connect(mix);
      k.osc('square', 845).connect(mix);
      k.toOut(mix, k.bp(2600, 1), k.env({ a: 0.001, d: 0.26, s: 0, r: 0, peak: 0.7 }));
    },
  },
  woodblock: {
    dur: 0.08, db: -6, render(k) {
      sineDrop(k, 1050, 980, 0.02, 0.0005, 0.045, 0.7);
      noiseHit(k, 'white', k.bp(2100, 4), 0.0005, 0.01, 0.3);
    },
  },
  // Phase-transition riser: a reverse cymbal that swells into the next downbeat (where the crash
  // lands). It peaks at its very end, faded over the last 4 ms so the cut never clicks.
  riser: {
    dur: 1.6, db: -13, render(k) {
      const end = 1.6;
      const swell = (peak: number): GainNode => {
        const g = k.gain(0);
        g.gain.setValueAtTime(0, 0);
        g.gain.setValueAtTime(0.0001, 0.002);
        g.gain.exponentialRampToValueAtTime(peak * 0.04, end * 0.45);
        g.gain.exponentialRampToValueAtTime(peak, end - 0.004);
        g.gain.linearRampToValueAtTime(0, end);
        return g;
      };
      const n = k.noise('white');
      const hp = k.hp(1800, 0.7);
      k.pitchEnv(hp.frequency, 1800, 6500, end, 'exp');
      k.toOut(n, hp, k.lp(12000, 0.5), swell(0.55));
      k.toOut(metal(k, 1.05), k.hp(4200), swell(0.3));
      // A whooshing band that climbs with it, so the swell reads as "up" rather than hiss.
      const band = k.noise('white');
      const bp = k.bp(420, 4);
      k.pitchEnv(bp.frequency, 420, 3600, end, 'exp');
      k.toOut(band, bp, swell(1.1));
    },
  },
  // Spin Cycle's crash: a soapy slosh.
  slosh: {
    dur: 0.9, db: -9, render(k) {
      const n = k.noise('pink');
      const lp = k.lp(500, 2);
      lp.frequency.setValueAtTime(500, 0);
      lp.frequency.exponentialRampToValueAtTime(2200, 0.15);
      lp.frequency.exponentialRampToValueAtTime(450, 0.8);
      k.toOut(n, lp, k.env({ a: 0.04, d: 0.8, s: 0, r: 0, peak: 0.9 }));
      for (let i = 0; i < 6; i++) {
        const t = 0.1 + k.rand() * 0.6;
        const f = k.range(300, 700);
        const o = k.osc('sine', f, t, t + 0.08);
        k.pitchEnv(o.frequency, f, f * 2, 0.05, 'exp', t);
        k.toOut(o, k.env({ a: 0.003, d: 0.05, s: 0, r: 0, peak: 0.12, at: t }));
      }
    },
  },
};

/** Reference MIDI notes (Cs) an instrument renders so every note in its range is within ±6. */
export function referenceNotes(def: InstrumentDef): number[] {
  const set = new Set<number>();
  for (let m = def.lo; m <= def.hi; m++) set.add(Math.round(m / 12) * 12);
  return [...set].sort((a, b) => a - b);
}

/** The reference note to play `midi` from. */
export function nearestReference(refs: readonly number[], midi: number): number {
  let best = refs[0];
  for (const r of refs) if (Math.abs(r - midi) < Math.abs(best - midi)) best = r;
  return best;
}
